import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import ApiClient from '../Services/ApiClient';
import { MeetingDTO } from '../../Models/MeetingModel';

export interface MeetingsState {
  byPatient: Record<number, { items: MeetingDTO[]; totalCount: number; loading?: boolean; error?: string | null; currentPage?: number }>;
  // global loading can be used for bulk operations; per-patient loading is stored in byPatient entries
  loading: boolean;
  error?: string | null;
}

const initialState: MeetingsState = {
  byPatient: {},
  loading: false,
  error: null
};

export const fetchMeetingsByPatient = createAsyncThunk(
  'meetings/fetchByPatient',
  async ({ patientId, page = 1, pageSize = 20 }: { patientId: number; page?: number; pageSize?: number }) => {
    const res = await ApiClient.get(`/meetings/patient/${patientId}?page=${page}&pageSize=${pageSize}`);
    return { patientId, page, result: res as { items: MeetingDTO[]; totalCount: number } };
  }
);

// Fetch meetings for the current logged-in user (uses /meetings/me) and stores under provided userId
export const fetchMyMeetings = createAsyncThunk(
  'meetings/fetchMy',
  async ({ userId, page = 1, pageSize = 20 }: { userId: number; page?: number; pageSize?: number }) => {
    const res = await ApiClient.get(`/meetings/me?page=${page}&pageSize=${pageSize}`);
    return { patientId: userId, page, result: res as { items: MeetingDTO[]; totalCount: number } };
  }
);

// addMeeting supports optimistic updates: pass { meeting, tempId }
export const addMeeting = createAsyncThunk('meetings/add', async ({ meeting }: { meeting: MeetingDTO; tempId?: number }) => {
  const res = await ApiClient.post('/meetings', meeting);
  return { serverMeeting: res as MeetingDTO };
});

export const sendWhatsAppForMeeting = createAsyncThunk(
  'meetings/sendWhatsappMeeting',
  async ({ meetingId, patientId, message }: { meetingId: number; patientId: number; message?: string }) => {
    await ApiClient.post(`/whatsapp/send/meeting/${meetingId}`, { PatientId: patientId, Message: message });
    return { meetingId };
  }
);

export const sendWhatsAppForPayment = createAsyncThunk(
  'meetings/sendWhatsappPayment',
  async ({ meetingId, patientId }: { meetingId: number; patientId: number }) => {
    await ApiClient.post(`/whatsapp/send/payment/${meetingId}`, { PatientId: patientId });
    return { meetingId };
  }
);

export const createZoomMeeting = createAsyncThunk(
  'meetings/createZoom',
  async ({ meetingId, meetingDate }: { meetingId: number; meetingDate: string }) => {
    const res = await ApiClient.post(`/meetings/${meetingId}/zoom`, { meetingDate });
    return { meetingId, data: res };
  }
);

export const updateMeeting = createAsyncThunk('meetings/update', async (payload: MeetingDTO) => {
  const res = await ApiClient.put(`/meetings/${payload.id}`, payload);
  return payload;
});

export const removeMeeting = createAsyncThunk('meetings/remove', async (id: number) => {
  await ApiClient.delete(`/meetings/${id}`);
  return id;
});

const slice = createSlice({
  name: 'meetings',
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchMeetingsByPatient.pending, (s, a) => {
        s.loading = true;
        s.error = null;
        const pid = (a.meta.arg as any)?.patientId as number | undefined;
        if (pid != null) s.byPatient[pid] = s.byPatient[pid] || { items: [], totalCount: 0 };
        if (pid != null) {
          s.byPatient[pid].loading = true;
          s.byPatient[pid].currentPage = (a.meta.arg as any)?.page ?? s.byPatient[pid].currentPage ?? 1;
        }
      })
      .addCase(fetchMeetingsByPatient.fulfilled, (s, a) => {
        s.loading = false;
        s.byPatient[a.payload.patientId] = { items: a.payload.result.items, totalCount: a.payload.result.totalCount, loading: false, error: null, currentPage: a.payload.page };
      })
      .addCase(fetchMeetingsByPatient.rejected, (s, a) => {
        s.loading = false;
        const pid = (a.meta.arg as any)?.patientId as number | undefined;
        if (pid != null) {
          s.byPatient[pid] = s.byPatient[pid] || { items: [], totalCount: 0 };
          s.byPatient[pid].loading = false;
          s.byPatient[pid].error = a.error.message || 'Failed';
        } else {
          s.error = a.error.message || 'Failed';
        }
      })
      // optimistic add: insert temp item on pending, replace on fulfilled, rollback on rejected
      .addCase(addMeeting.pending, (s, a) => {
        const arg = a.meta.arg as any;
        const meeting: MeetingDTO = arg?.meeting;
        const tempId: number = arg?.tempId ?? -(Date.now());
        const p = meeting?.patientId ?? 0;
        s.byPatient[p] = s.byPatient[p] || { items: [], totalCount: 0 };
        const temp = { ...meeting, id: tempId } as MeetingDTO;
        s.byPatient[p].items.unshift(temp);
        s.byPatient[p].totalCount = (s.byPatient[p].totalCount || 0) + 1;
      })
      .addCase(addMeeting.fulfilled, (s, a) => {
        const server = a.payload.serverMeeting as MeetingDTO;
        const p = server.patientId;
        s.byPatient[p] = s.byPatient[p] || { items: [], totalCount: 0 };
        // try to find a temp item (negative id) and replace it
        const idx = s.byPatient[p].items.findIndex(m => m.id < 0 || m.id === server.id);
        if (idx >= 0) {
          s.byPatient[p].items[idx] = server;
        } else {
          s.byPatient[p].items.unshift(server);
          s.byPatient[p].totalCount = (s.byPatient[p].totalCount || 0) + 1;
        }
      })
      .addCase(addMeeting.rejected, (s, a) => {
        const arg = a.meta.arg as any;
        const meeting: MeetingDTO = arg?.meeting;
        const tempId: number = arg?.tempId ?? undefined;
        const p = meeting?.patientId ?? 0;
        if (s.byPatient[p]) {
          // remove the temp item
          const idx = s.byPatient[p].items.findIndex(m => (tempId !== undefined ? m.id === tempId : m.id < 0));
          if (idx >= 0) {
            s.byPatient[p].items.splice(idx, 1);
            s.byPatient[p].totalCount = Math.max(0, (s.byPatient[p].totalCount || 1) - 1);
          }
          s.byPatient[p].error = a.error.message || 'Failed to add meeting';
        }
      })

      .addCase(updateMeeting.fulfilled, (s, a) => {
        const p = a.payload.patientId;
        if (s.byPatient[p]) s.byPatient[p].items = s.byPatient[p].items.map(m => m.id === a.payload.id ? a.payload : m);
      })
      .addCase(removeMeeting.fulfilled, (s, a) => {
        Object.values(s.byPatient).forEach(group => {
          const idx = group.items.findIndex(m => m.id === a.payload);
          if (idx >= 0) { group.items.splice(idx, 1); group.totalCount = Math.max(0, group.totalCount - 1); }
        });
      });
  }
});

export default slice.reducer;